import React, { useState, useEffect } from 'react';
import { Clock, AlertCircle, X, Check, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import VoiceCommandPanel from './VoiceCommandPanel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

interface Room {
  id: string;
  code: string;
  name: string;
  status: string;
}

interface PatientOption {
  id: string;
  fullName: string;
  noticeNumber?: string | null;
  roomId?: string | null;
  status?: string;
  procedureName?: string | null;
  surgeonName?: string | null;
  attendanceNumber?: string | null;
  birthDate?: string | null;
  allergies?: string | null;
  estimatedMinutes?: number | null;
}

interface SetupTimes {
  transportStart?: string;
  transportEnd?: string;
  surgeryStart?: string;
  surgeryEnd?: string;
  anesthesiaStart?: string;
  anesthesiaEnd?: string;
  cleaningStart?: string;
  cleaningEnd?: string;
  equipmentStart?: string;
  equipmentEnd?: string;
  anesthesiologistStart?: string;
  anesthesiologistEnd?: string;
  nurseStart?: string;
  nurseEnd?: string;
  surgicalTeamStart?: string;
  surgicalTeamEnd?: string;
}

interface RoomSetup extends Room {
  patientName?: string;
  times?: SetupTimes;
  scheduledStart?: string;
  estimatedMinutes?: number | null;
  allergies?: string | null;
  isDelayed?: boolean;
  delayReason?: string;
  procedureName?: string;
  surgeonName?: string;
  caseId?: string;
}

type TimelineActionKey = 'start' | 'end' | 'in' | 'out';

interface TimelineStage {
  key: string;
  label: string;
  kind: 'start_end' | 'in_out';
  seq: number;
  actions: Array<{ label: string; action: TimelineActionKey }>;
}

export default function SetupSala() {
  const [rooms, setRooms] = useState<RoomSetup[]>([]);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [showDelayPopup, setShowDelayPopup] = useState(false);
  const [delayReason, setDelayReason] = useState('');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [openingRoomId, setOpeningRoomId] = useState<string | null>(null);
  const [caseEvents, setCaseEvents] = useState<any[]>([]);
  const [editingRoom, setEditingRoom] = useState<RoomSetup | null>(null);
  const [editingRoomPatientId, setEditingRoomPatientId] = useState<string>('');
  const [editingRoomPatientQuery, setEditingRoomPatientQuery] = useState<string>('');
  const [editEventId, setEditEventId] = useState<string | null>(null);
  const [editEventTime, setEditEventTime] = useState<string>('');
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulePatientId, setSchedulePatientId] = useState<string | null>(null);
  const [scheduleTime, setScheduleTime] = useState('07:00');
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
  const [closingRoom, setClosingRoom] = useState<RoomSetup | null>(null);
  const [closingRoomMode, setClosingRoomMode] = useState<'case' | 'room_setup' | null>(null);
  const [timelineStages, setTimelineStages] = useState<TimelineStage[]>([]);

  const delayReasons = [
    'Atraso no transporte',
    'Atraso Equipe Médica',
    'Atraso Equipe Anestesiologia',
    'Atraso Equipe Enfermagem',
    'Atraso Equipamento',
    'Atraso Preparação Sala',
    'Outro motivo'
  ];

  const timeFields = [
    { key: 'transportStart', label: 'TRANSPORTE', subLabel: 'Saída da Unidade' },
    { key: 'transportEnd', label: 'PACIENTE ENTRADA', subLabel: 'Entrada no CC' },
    { key: 'surgeryStart', label: 'CIRURGIA', subLabel: 'Início/Fim' },
    { key: 'anesthesiaStart', label: 'ANESTESIA', subLabel: 'Início/Fim' },
    { key: 'positioningStart', label: 'POSICIONAMENTO', subLabel: 'Início/Fim' },
    { key: 'timeOutStart', label: 'TIME OUT', subLabel: 'Início/Fim' },
    { key: 'cleaningStart', label: 'LIMPEZA', subLabel: 'Entrada/Saída' },
    { key: 'equipmentStart', label: 'FARMÁCIA', subLabel: 'Entrada/Saída' },
    { key: 'surgicalTeamStart', label: 'ENG CLÍNICA', subLabel: 'Entrada/Saída' }
  ];

  useEffect(() => {
    fetchRooms();
    fetchTimelineStages();
  }, []);

  const fetchTimelineStages = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/timeline-stages`, { headers });
      const stages = (response.data || [])
        .filter((s: any) => s.active)
        .map((s: any) => ({
          seq: s.seq,
          key: s.key,
          label: s.label,
          kind: s.kind,
          actions: s.kind === 'start_end'
            ? [{ label: 'Início', action: 'start' as TimelineActionKey }, { label: 'Fim', action: 'end' as TimelineActionKey }]
            : [{ label: 'Entrada', action: 'in' as TimelineActionKey }, { label: 'Saída', action: 'out' as TimelineActionKey }]
        }))
        .sort((a: TimelineStage, b: TimelineStage) => a.seq - b.seq);
      setTimelineStages(stages);
    } catch (error) {
      console.error('Erro ao carregar etapas do fluxo:', error);
      // Fallback to defaults if API fails
      setTimelineStages([
        { seq: 1, key: 'transport_patient', label: 'Transporte do paciente', kind: 'start_end', actions: [{ label: 'Início', action: 'start' }, { label: 'Fim', action: 'end' }] },
        { seq: 2, key: 'admission_cc', label: 'Admissão no Pré CC', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 3, key: 'patient_in_or', label: 'Paciente em SO', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 4, key: 'anesthesia_team', label: 'Equipe anestésica', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 5, key: 'surgical_team', label: 'Equipe cirúrgica', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 6, key: 'anesthesia', label: 'Anestesia', kind: 'start_end', actions: [{ label: 'Início', action: 'start' }, { label: 'Fim', action: 'end' }] },
        { seq: 7, key: 'positioning', label: 'Posicionamento', kind: 'start_end', actions: [{ label: 'Início', action: 'start' }, { label: 'Fim', action: 'end' }] },
        { seq: 8, key: 'time_out', label: 'Time out', kind: 'start_end', actions: [{ label: 'Início', action: 'start' }, { label: 'Fim', action: 'end' }] },
        { seq: 9, key: 'surgery', label: 'Cirurgia', kind: 'start_end', actions: [{ label: 'Início', action: 'start' }, { label: 'Fim', action: 'end' }] },
        { seq: 10, key: 'cme', label: 'CME', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 11, key: 'cleaning', label: 'Limpeza', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 12, key: 'pharmacy', label: 'Farmácia', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 13, key: 'clinical_engineering', label: 'Eng. clínica', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 14, key: 'rpa', label: 'RPA', kind: 'in_out', actions: [{ label: 'Entrada', action: 'in' }, { label: 'Saída', action: 'out' }] },
        { seq: 15, key: 'room_setup', label: 'Montagem sala', kind: 'start_end', actions: [{ label: 'Início', action: 'start' }, { label: 'Fim', action: 'end' }] },
      ]);
    }
  };

  useEffect(() => {
    const activeRoom = rooms.find((room) => room.id === selectedRoom);
    if (!activeRoom?.caseId) {
      setCaseEvents([]);
      return;
    }

    fetchCaseEvents(activeRoom.caseId);
  }, [rooms, selectedRoom]);

  const fetchRooms = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [roomsResponse, casesResponse, patientsResponse] = await Promise.all([
        axios.get(`${API_URL}/rooms`, { headers }),
        axios.get(`${API_URL}/cases`, { headers }),
        axios.get(`${API_URL}/patients`, { headers })
      ]);

      const scheduledPatients = Array.isArray(patientsResponse.data)
        ? patientsResponse.data.filter((patient: any) => patient.roomId && patient.status === 'scheduled')
        : [];

      const activeCasesByRoom = new Map<string, any>();
      casesResponse.data
        .filter((item: any) => item.status === 'active')
        .forEach((item: any) => activeCasesByRoom.set(item.roomId, item));

      const roomsNeedingSync = roomsResponse.data.filter((room: Room) => {
        const hasActiveCase = activeCasesByRoom.has(room.id);
        const hasScheduledPatient = scheduledPatients.some((patient: any) => patient.roomId === room.id);
        return !hasActiveCase && hasScheduledPatient;
      });

      if (roomsNeedingSync.length > 0) {
        const syncedCases = await Promise.all(
          roomsNeedingSync.map(async (room: Room) => {
            try {
              const response = await axios.get(`${API_URL}/rooms/${room.id}/case`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              return response.data;
            } catch {
              return null;
            }
          })
        );

        syncedCases.forEach((item) => {
          if (item?.roomId) {
            activeCasesByRoom.set(item.roomId, item);
          }
        });
      }

      const roomsWithSetup: RoomSetup[] = roomsResponse.data.map((room: Room) => {
        const activeCase = activeCasesByRoom.get(room.id);
        const scheduledPatient = scheduledPatients.find((patient: any) => patient.roomId === room.id);

        return {
          ...room,
          caseId: activeCase?.id,
          patientName: activeCase?.patientFullName || 'Não informado',
          procedureName: activeCase?.procedureName || 'Procedimento não informado',
          surgeonName: activeCase?.surgeonName || 'Cirurgião não informado',
          scheduledStart: activeCase?.plannedSurgeryTime || '—',
          estimatedMinutes: scheduledPatient?.estimatedMinutes ?? null,
          allergies: activeCase?.allergies || scheduledPatient?.allergies || null,
          delayReason: activeCase?.delayReason || undefined,
          times: {}
        };
      });
      setRooms(roomsWithSetup);
      setPatients(Array.isArray(patientsResponse.data) ? patientsResponse.data : []);

      const roomToOpen = localStorage.getItem('setupRoomId');
      if (roomToOpen) {
        setSelectedRoom(roomToOpen);
        localStorage.removeItem('setupRoomId');
      }

      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar salas:', error);
      setLoading(false);
    }
  };

  const handleOpenRoomCase = async (roomId: string) => {
    try {
      setOpeningRoomId(roomId);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/rooms/${roomId}/case`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.data) {
        alert('Nenhum paciente agendado para esta sala. Agende um paciente primeiro.');
        return;
      }
      await fetchRooms();
      setSelectedRoom(roomId);
    } catch (error) {
      console.error('Erro ao abrir caso da sala:', error);
    } finally {
      setOpeningRoomId(null);
    }
  };

  const handleEditRoom = async (room: RoomSetup) => {
    // Check if patient already entered SO - if so, block editing
    if (room.caseId) {
      const roomEvents = caseEvents.filter((e) => e.caseId === room.caseId);
      const hasPatientInSO = roomEvents.some((e) => e.eventKey === 'patient_in_or' && e.action === 'in');
      if (hasPatientInSO) {
        alert('Não é possível editar a sala. O paciente já está em SO (entrada registrada).');
        return;
      }
    }

    setEditingRoom(room);
    setEditValue(room.name || '');
    // ensure patients list is loaded
    if (!patients || patients.length === 0) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/patients`, { headers: { Authorization: `Bearer ${token}` } });
        setPatients(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Erro ao carregar pacientes para edição:', err);
      }
    }

    const currentPatient = (patients || []).find((patient) => patient.roomId === room.id && patient.status === 'scheduled');
    setEditingRoomPatientId(currentPatient?.id || '');
  };

  const submitEditRoom = async () => {
    if (!editingRoom) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.patch(`${API_URL}/rooms/${editingRoom.id}`, { name: editValue }, { headers });
      setRooms((current) => current.map((r) => r.id === response.data.id ? { ...r, name: response.data.name } : r));

      if (editingRoomPatientId) {
        // Check if patient is already in another room
        const selectedPatient = patients.find((p) => p.id === editingRoomPatientId);
        if (selectedPatient && selectedPatient.roomId && selectedPatient.roomId !== editingRoom.id && selectedPatient.status === 'scheduled') {
          const existingRoom = rooms.find((r) => r.id === selectedPatient.roomId);
          alert(`Paciente já está alocado na sala ${existingRoom?.code || existingRoom?.name || 'outra'}. Conclua o caso atual antes de mover para outra sala.`);
          return;
        }

        // assign patient to room and mark as scheduled
        try {
          const patientPatch = await axios.patch(
            `${API_URL}/patients/${editingRoomPatientId}`,
            { roomId: editingRoom.id, status: 'scheduled' },
            { headers }
          );

          // also update the active case with patient details if case exists
          if (editingRoom.caseId) {
            const patientData = patientPatch.data;
            await axios.patch(
              `${API_URL}/cases/${editingRoom.caseId}`,
              {
                patientFullName: patientData.fullName || null,
                noticeNumber: patientData.noticeNumber || null,
                procedureName: patientData.procedureName || null,
                surgeonName: patientData.surgeonName || null,
                attendanceNumber: patientData.attendanceNumber || null,
                birthDate: patientData.birthDate || null,
                allergies: patientData.allergies || null
              },
              { headers }
            );
          }
        } catch (err: any) {
          const errorMsg = err?.response?.data?.message;
          if (errorMsg) {
            alert(errorMsg);
            return;
          }
          throw err;
        }
      }

      setEditingRoom(null);
      setEditValue('');
      setEditingRoomPatientId('');
      await fetchRooms();
    } catch (error) {
      console.error('Erro ao editar sala:', error);
    }
  };

  const handleCloseCase = async (room: RoomSetup) => {
    if (!room.caseId) return;
    setClosingRoom(room);
    setClosingRoomMode('case');
    setShowCloseConfirmModal(true);
  };

  const confirmCloseCase = async () => {
    if (!closingRoom?.caseId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const happenedAt = new Date().toISOString();

      if (closingRoomMode === 'room_setup') {
        // Register "end" of room_setup before closing
        await axios.post(
          `${API_URL}/events`,
          { caseId: closingRoom.caseId, eventKey: 'room_setup', action: 'end', happenedAt },
          { headers }
        );
      }
      // Request server to close the case, auto-end all open stages, release patient, and clear room
      await axios.post(`${API_URL}/cases/${closingRoom.caseId}/close`, {}, { headers });
      
      // Clear local state and refresh
      setCaseEvents([]);
      if (selectedRoom === closingRoom.id) {
        setSelectedRoom(null);
      }
      await fetchRooms();
    } catch (error) {
      console.error('Erro ao concluir caso:', error);
    } finally {
      setShowCloseConfirmModal(false);
      setClosingRoom(null);
      setClosingRoomMode(null);
    }
  };

  const openScheduleModal = (roomId?: string) => {
    setShowScheduleModal(true);
    if (roomId) {
      // pre-select any patient assigned to this room if present
    }
  };

  const submitSchedule = async () => {
    if (!schedulePatientId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/schedules`, { patientId: schedulePatientId, roomId: selectedRoom, scheduledStart: `${new Date().toISOString().slice(0,10)}T${scheduleTime}:00`, estimatedMinutes: 60 }, { headers });
      setShowScheduleModal(false);
      setSchedulePatientId(null);
      await fetchRooms();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message;
      if (errorMsg) {
        alert(errorMsg);
      } else {
        console.error('Erro ao criar agendamento:', error);
      }
    }
  };

  const fetchCaseEvents = async (caseId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cases/${caseId}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseEvents(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar eventos da sala:', error);
    }
  };

  const recordEvent = async (caseId: string, eventKey: string, action: 'start' | 'end' | 'in' | 'out') => {
    try {
      const token = localStorage.getItem('token');
      const skipAuto = eventKey === 'cleaning';
      // Send client timestamp to avoid server timezone issues
      const happenedAt = new Date().toISOString();
      await axios.post(
        `${API_URL}/events`,
        { caseId, eventKey, action, skipAutoRules: skipAuto, happenedAt },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchRooms();

      const active = rooms.find((room) => room.id === selectedRoom);
      if (active?.caseId) {
        await fetchCaseEvents(active.caseId);
      }
    } catch (error: any) {
      console.error('Erro ao registrar evento:', error);
    }
  };

  const openEditEventModal = (event: any) => {
    setEditEventId(event.id);
    const d = new Date(event.happenedAt);
    setEditEventTime(d.toISOString().slice(0,19));
    setShowEditEventModal(true);
  };

  const submitEditEvent = async () => {
    if (!editEventId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/events/${editEventId}`, { happenedAt: new Date(editEventTime).toISOString() }, { headers: { Authorization: `Bearer ${token}` } });
      if (selectedRoom) {
        const active = rooms.find((r) => r.id === selectedRoom);
        if (active?.caseId) await fetchCaseEvents(active.caseId);
      }
      await fetchRooms();
    } catch (error) {
      console.error('Erro ao editar evento:', error);
    } finally {
      setShowEditEventModal(false);
      setEditEventId(null);
      setEditEventTime('');
    }
  };

  const getStageEvents = (caseId: string | undefined, stageKey: string) => {
    if (!caseId) return [];
    return caseEvents
      .filter((event) => event.caseId === caseId && event.eventKey === stageKey)
      .sort((a, b) => new Date(a.happenedAt).getTime() - new Date(b.happenedAt).getTime());
  };

  const formatEventTime = (value?: string) => {
    if (!value) return '—';
    return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getStageStatus = (stage: TimelineStage, events: any[]) => {
    const startEvent = events.find((event) => event.action === (stage.kind === 'start_end' ? 'start' : 'in'));
    const endEvent = events.find((event) => event.action === (stage.kind === 'start_end' ? 'end' : 'out'));

    if (startEvent && endEvent) return 'done';
    if (startEvent && !endEvent) return 'active';
    return 'pending';
  };

  const getStageButtonDisabled = (stage: TimelineStage, events: any[], action: TimelineActionKey) => {
    const startAction = stage.kind === 'start_end' ? 'start' : 'in';
    const endAction = stage.kind === 'start_end' ? 'end' : 'out';
    if (action === startAction) {
      return Boolean(events.find((event) => event.action === startAction));
    }
    return !events.find((event) => event.action === startAction) || Boolean(events.find((event) => event.action === endAction));
  };

  const stageBadgeClass = (status: 'done' | 'active' | 'pending') => {
    if (status === 'done') return 'bg-green-100 text-green-700';
    if (status === 'active') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
  };

  const stageBorderClass = (status: 'done' | 'active' | 'pending') => {
    if (status === 'done') return 'border-green-300 bg-green-50';
    if (status === 'active') return 'border-amber-300 bg-amber-50';
    return 'border-slate-200 bg-white';
  };

  const formatDuration = (ms?: number | null) => {
    if (ms === null || ms === undefined || Number.isNaN(ms) || ms <= 0) return '—';
    const totalSeconds = Math.floor(Math.max(0, ms) / 1000);
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const getStageDuration = (caseId: string | undefined, stage: TimelineStage) => {
    const stageEvents = getStageEvents(caseId, stage.key);
    const startAction = stage.kind === 'start_end' ? 'start' : 'in';
    const endAction = stage.kind === 'start_end' ? 'end' : 'out';
    const startEvent = stageEvents.find((event) => event.action === startAction);
    const endEvent = stageEvents.find((event) => event.action === endAction);
    if (!startEvent || !endEvent) return null;
    return new Date(endEvent.happenedAt).getTime() - new Date(startEvent.happenedAt).getTime();
  };

  const getRecentMovements = (caseId: string | undefined) => {
    if (!caseId) return [];
    return caseEvents
      .filter((event) => event.caseId === caseId)
      .sort((a, b) => new Date(b.happenedAt).getTime() - new Date(a.happenedAt).getTime())
      .slice(0, 12);
  };

  const handleStageAction = async (room: RoomSetup, stage: TimelineStage, action: TimelineActionKey) => {
    if (!room.caseId) return;

    // Rule: "Montagem de Sala" FIM triggers the close-all modal
    if (stage.key === 'room_setup' && action === (stage.kind === 'start_end' ? 'end' : 'out')) {
      setClosingRoom(room);
      setClosingRoomMode('room_setup');
      setShowCloseConfirmModal(true);
      return;
    }

    // Rule: "Limpeza" ENTRADA - if patient still in SO, ask to give exit
    if (stage.key === 'cleaning' && action === 'in') {
      const patientInOrEvents = getStageEvents(room.caseId, 'patient_in_or');
      const hasPatientIn = patientInOrEvents.some((e) => e.action === 'in');
      const hasPatientOut = patientInOrEvents.some((e) => e.action === 'out');
      if (hasPatientIn && !hasPatientOut) {
        const confirmExit = window.confirm(
          'O paciente ainda está em SO. Deseja registrar a SAÍDA do paciente da SO antes de iniciar a limpeza?'
        );
        if (confirmExit) {
          await recordEvent(room.caseId, 'patient_in_or', 'out');
        }
      }
    }

    // Rule: "RPA" ENTRADA - auto exit patient from SO if still there
    if (stage.key === 'rpa' && action === 'in') {
      const patientInOrEvents = getStageEvents(room.caseId, 'patient_in_or');
      const hasPatientIn = patientInOrEvents.some((e) => e.action === 'in');
      const hasPatientOut = patientInOrEvents.some((e) => e.action === 'out');
      if (hasPatientIn && !hasPatientOut) {
        await recordEvent(room.caseId, 'patient_in_or', 'out');
      }
    }

    await recordEvent(room.caseId, stage.key, action);
  };

  // --- Voice commands: reuse the exact same validation + handler as the buttons
  const voiceCheckAction = (caseId: string, stageKey: string, action: TimelineActionKey) => {
    const stage = timelineStages.find((s) => s.key === stageKey);
    if (!stage) return { allowed: false, reason: 'etapa não encontrada' };
    const events = getStageEvents(caseId, stageKey);
    const disabled = getStageButtonDisabled(stage, events, action);
    if (!disabled) return { allowed: true };
    const startAction = stage.kind === 'start_end' ? 'start' : 'in';
    if (events.some((e) => e.action === action)) return { allowed: false, reason: 'já registrado' };
    return { allowed: false, reason: action === startAction ? 'indisponível agora' : 'registre o início primeiro' };
  };

  const voiceRunAction = async (roomId: string, stageKey: string, action: TimelineActionKey) => {
    const room = rooms.find((r) => r.id === roomId);
    const stage = timelineStages.find((s) => s.key === stageKey);
    if (room && stage) await handleStageAction(room, stage, action);
  };

  const calculateDelay = (room: RoomSetup): { isDelayed: boolean; minutes: number } => {
    if (!room.scheduledStart || !room.times?.transportEnd) {
      return { isDelayed: false, minutes: 0 };
    }

    const [scheduledHour, scheduledMin] = room.scheduledStart.split(':').map(Number);
    const scheduledTime = scheduledHour * 60 + scheduledMin;

    const [actualHour, actualMin] = room.times.transportEnd.split(':').map(Number);
    const actualTime = actualHour * 60 + actualMin;

    const difference = actualTime - scheduledTime;
    return { isDelayed: difference > 0, minutes: Math.abs(difference) };
  };

  const getStatusColor = (room: RoomSetup) => {
    const status = getStatusText(room);
    switch (status) {
      case 'EM CIRURGIA': return 'bg-red-100 border-red-400';
      case 'PACIENTE EM SO': return 'bg-amber-100 border-amber-400';
      case 'EM TRANSPORTE': return 'bg-blue-100 border-blue-400';
      case 'EM LIMPEZA': return 'bg-yellow-100 border-yellow-400';
      case 'PÓS-CIRURGIA': return 'bg-purple-100 border-purple-400';
      case 'SALA PRONTA': return 'bg-green-100 border-green-400';
      case 'ESCALADO': return 'bg-cyan-100 border-cyan-400';
      case 'LIVRE': return 'bg-green-50 border-green-300';
      default: return 'bg-slate-100 border-slate-300';
    }
  };

  const getStatusText = (room: RoomSetup) => {
    // Use actual case events to determine status
    if (!room.caseId) {
      return room.patientName && room.patientName !== 'Não informado' ? 'ESCALADO' : 'LIVRE';
    }

    const roomEvents = caseEvents.filter((e) => e.caseId === room.caseId);

    const hasRoomSetupEnd = roomEvents.some((e) => e.eventKey === 'room_setup' && e.action === 'end');
    const hasCleaningIn = roomEvents.some((e) => e.eventKey === 'cleaning' && e.action === 'in');
    const hasCleaningOut = roomEvents.some((e) => e.eventKey === 'cleaning' && e.action === 'out');
    const hasSurgeryStart = roomEvents.some((e) => e.eventKey === 'surgery' && e.action === 'start');
    const hasSurgeryEnd = roomEvents.some((e) => e.eventKey === 'surgery' && e.action === 'end');
    const hasPatientIn = roomEvents.some((e) => e.eventKey === 'patient_in_or' && e.action === 'in');
    const hasPatientOut = roomEvents.some((e) => e.eventKey === 'patient_in_or' && e.action === 'out');
    const hasTransportStart = roomEvents.some((e) => e.eventKey === 'transport_patient' && e.action === 'start');

    if (hasRoomSetupEnd) return 'SALA PRONTA';
    if (hasCleaningIn && !hasCleaningOut) return 'EM LIMPEZA';
    if (hasSurgeryEnd && hasPatientOut) return 'PÓS-CIRURGIA';
    if (hasSurgeryStart && !hasSurgeryEnd) return 'EM CIRURGIA';
    if (hasPatientIn && !hasPatientOut) return 'PACIENTE EM SO';
    if (hasTransportStart && !hasPatientIn) return 'EM TRANSPORTE';
    if (room.patientName && room.patientName !== 'Não informado') return 'ESCALADO';
    return 'AGUARDANDO';
  };

  const handleTimeUpdate = async (roomId: string, field: string, value: string) => {
    try {
      const token = localStorage.getItem('token');
      const room = rooms.find(r => r.id === roomId);
      if (!room) return;

      const updatedRoom = {
        ...room,
        times: { ...room.times, [field]: value }
      };

      // Check for delay and show popup if needed
      const { isDelayed } = calculateDelay(updatedRoom);
      if (isDelayed && field === 'transportEnd') {
        setSelectedRoom(roomId);
        setShowDelayPopup(true);
      }

      // Update locally
      setRooms(rooms.map(r => r.id === roomId ? updatedRoom : r));
      
      // API call would go here
      // await axios.patch(`${API_URL}/rooms/${roomId}`, { times: updatedRoom.times }, { headers });
    } catch (error) {
      console.error('Erro ao atualizar tempo:', error);
    }
  };

  const handleDelaySubmit = () => {
    const room = rooms.find((item) => item.id === selectedRoom);

    if (!room?.caseId) {
      setShowDelayPopup(false);
      setDelayReason('');
      return;
    }

    const token = localStorage.getItem('token');
    axios.patch(
      `${API_URL}/cases/${room.caseId}`,
      { delayReason },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(() => {
        setRooms((current) => current.map((item) => (
          item.id === selectedRoom ? { ...item, delayReason } : item
        )));
      })
      .catch((error) => {
        console.error('Erro ao salvar justificativa do atraso:', error);
      })
      .finally(() => {
        setShowDelayPopup(false);
        setDelayReason('');
      });
  };

  if (loading) return <div className="text-center py-12">Carregando...</div>;

  const activeRoom = rooms.find(r => r.id === selectedRoom);

  const selectedRoomCaseEvents = activeRoom?.caseId ? caseEvents.filter((event) => event.caseId === activeRoom.caseId) : [];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="card shadow-sm p-4 border border-slate-200 hero rounded-2xl bg-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Clock className="text-slate-700" size={28} />
              <h1 className="text-2xl md:text-3xl font-black text-slate-900">Setup de Sala - Tempos e Movimentos</h1>
            </div>
            <p className="text-sm text-slate-600 mt-1">Acompanhe sala, paciente, procedimento e tempos de forma operacional.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="chip bg-slate-100 text-slate-900 border border-slate-200">Salas</span>
            <span className="chip bg-slate-100 text-slate-900 border border-slate-200">Ações em tempo real</span>
          </div>
        </div>
      </div>

      {/* Grid de Salas - múltiplas por linha quando colapsadas */}
      {/* Mini cards das outras salas quando uma está expandida */}
      {selectedRoom && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {rooms.filter((r) => r.id !== selectedRoom).map((room) => {
            const { isDelayed } = calculateDelay(room);
            return (
              <div
                key={room.id}
                onClick={() => setSelectedRoom(room.id)}
                className={`border-2 rounded-xl p-3 transition-all cursor-pointer hover:shadow-md ${getStatusColor(room)}`}
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-black text-sm">{room.code}</h3>
                  <ChevronDown size={14} className="text-slate-400" />
                </div>
                <p className="text-xs text-slate-600 truncate mt-1">{room.patientName || '—'}</p>
                <div className={`mt-2 px-2 py-0.5 rounded text-[10px] font-bold inline-block ${
                  isDelayed ? 'bg-red-200 text-red-900' : 'bg-green-200 text-green-900'
                }`}>
                  {getStatusText(room)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={selectedRoom ? '' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'}>
        {rooms.map((room) => {
          const { isDelayed, minutes } = calculateDelay(room);
          const roomCaseEvents = room.caseId ? caseEvents.filter((e) => e.caseId === room.caseId) : [];
          const expanded = selectedRoom === room.id;

          // If another room is expanded, it's already shown in the mini grid above
          if (selectedRoom && !expanded) {
            return null;
          }
          
          return (
            <div
              key={room.id}
              className={`border-2 rounded-2xl p-4 transition-all ${getStatusColor(room)}`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-start gap-3">
                  <div>
                    <h3 className="font-black text-lg">{room.code}</h3>
                    <p className="text-sm font-bold text-slate-900">{room.name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="hidden sm:inline text-xs font-bold bg-white px-2 py-1 rounded-full">
                    {room.times?.transportStart ? '1' : '—'}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedRoom(expanded ? null : room.id); }}
                    className="p-2 rounded-full hover:bg-slate-100"
                    aria-label={expanded ? 'Fechar detalhes' : 'Abrir detalhes'}
                  >
                    {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
              </div>

              <div className="mt-2 space-y-1">
                <p className="text-xs text-slate-600">
                  <strong>Paciente:</strong> {room.patientName || 'Não informado'}
                </p>
                <p className="text-xs text-slate-600">
                  <strong>Procedimento:</strong> {room.procedureName || '—'}
                </p>
                <p className="text-xs text-slate-600">
                  <strong>Cirurgião:</strong> {room.surgeonName || '—'}
                </p>
                <p className="text-xs text-slate-600">
                  <strong>Hora Prevista:</strong> {room.scheduledStart || '—'}
                </p>
                <div className={`mt-2 px-2 py-1 rounded text-xs font-bold ${
                  isDelayed ? 'bg-red-200 text-red-900' : 'bg-green-200 text-green-900'
                }`}>
                  {getStatusText(room)}
                </div>
              </div>

              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleOpenRoomCase(room.id); }}
                  className="sm:flex-1 w-full sm:w-auto bg-blue-700 hover:bg-blue-800 text-white text-xs font-black px-3 py-2 rounded-full"
                >
                  {openingRoomId === room.id ? 'Abrindo...' : 'Abrir'}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleEditRoom(room); }}
                  className="sm:flex-1 w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white text-xs font-black px-3 py-2 rounded-full"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleCloseCase(room); }}
                  className="sm:flex-1 w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black px-3 py-2 rounded-full"
                >
                  Concluir
                </button>
              </div>

              {isDelayed && (
                <div className="mt-2 flex items-center gap-1 text-red-600 text-xs font-bold">
                  <AlertCircle size={14} />
                  Justificar atraso
                </div>
              )}

              {/* Inline expanded details */}
              {expanded && (
                <div className="mt-4 bg-white rounded-2xl border-2 border-green-400 p-4 shadow-sm">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-black text-slate-900">{room.code} - {room.patientName}</h2>
                    <button
                      onClick={() => setSelectedRoom(null)}
                      className="p-2 hover:bg-slate-100 rounded-lg"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleOpenRoomCase(room.id)}
                    className="mb-4 w-full bg-green-600 hover:bg-green-700 text-white font-black py-2 px-4 rounded-full transition-all"
                  >
                    {openingRoomId === room.id ? 'Sincronizando...' : 'Abrir / sincronizar caso ativo'}
                  </button>

                  <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                      <p className="text-xs text-slate-500">PACIENTE</p>
                      <p className="font-bold text-slate-900">{room.patientName || 'Não informado'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                      <p className="text-xs text-slate-500">PROCEDIMENTO</p>
                      <p className="font-bold text-slate-900">{room.procedureName || '—'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                      <p className="text-xs text-slate-500">CIRURGIÃO</p>
                      <p className="font-bold text-slate-900">{room.surgeonName || '—'}</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                      <p className="text-xs text-amber-700">ALERGIA</p>
                      <p className="font-bold text-amber-900">{room.allergies || 'Sem alergia registrada'}</p>
                    </div>
                  </div>

                  {calculateDelay(room).isDelayed && (
                    <div className="mb-4 bg-red-100 border-l-4 border-red-500 p-3 rounded">
                      <p className="text-sm font-bold text-red-800">
                        ⚠️ Atraso de {calculateDelay(room).minutes} minutos detectado
                        {room.delayReason && ` - Motivo: ${room.delayReason}`}
                      </p>
                    </div>
                  )}

                  <div className="mb-4 bg-slate-50 rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="text-sm font-black text-slate-900">Linha do tempo da execução</p>
                        <p className="text-xs text-slate-500">Cada etapa mostra o estado atual e o próximo registro esperado.</p>
                      </div>
                      <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-700">
                        {roomCaseEvents.length} eventos
                      </span>
                    </div>

                    <div className="space-y-3">
                      {timelineStages.map((stage, index) => {
                        const stageEvents = getStageEvents(room.caseId, stage.key);
                        const status = getStageStatus(stage, stageEvents);
                        const startEvent = stageEvents.find((event) => event.action === (stage.kind === 'start_end' ? 'start' : 'in'));
                        const endEvent = stageEvents.find((event) => event.action === (stage.kind === 'start_end' ? 'end' : 'out'));
                        const duration = getStageDuration(room.caseId, stage);

                        return (
                          <div key={stage.key} className={`relative rounded-xl border p-4 ${stageBorderClass(status)}`}>
                            {index < timelineStages.length - 1 && (
                              <div className="absolute left-6 top-12 bottom-0 w-px bg-slate-200" />
                            )}

                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between relative z-10">
                              <div className="flex items-start gap-3">
                                <div className={`mt-1 h-4 w-4 rounded-full border-2 ${status === 'done' ? 'bg-green-500 border-green-500' : status === 'active' ? 'bg-amber-500 border-amber-500' : 'bg-white border-slate-300'}`} />
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-black text-slate-900">{stage.seq}. {stage.label}</p>
                                    <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${stageBadgeClass(status)}`}>
                                      {status === 'done' ? 'Concluída' : status === 'active' ? 'Em andamento' : 'Pendente'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1">
                                    Início: {formatEventTime(startEvent?.happenedAt)}
                                    {stage.kind === 'start_end' ? ` • Fim: ${formatEventTime(endEvent?.happenedAt)}` : ` • Saída: ${formatEventTime(endEvent?.happenedAt)}`}
                                  </p>
                                </div>
                              </div>

                              <div className="w-full sm:w-auto sm:min-w-[240px]">
                                <div className="grid grid-cols-2 gap-2">
                                  {stage.actions.map((action) => {
                                    const disabled = !room.caseId || getStageButtonDisabled(stage, stageEvents, action.action);
                                    const isPrimary = action.action === (stage.kind === 'start_end' ? 'start' : 'in');
                                    return (
                                      <button
                                        key={`${stage.key}-${action.action}`}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => room.caseId && handleStageAction(room, stage, action.action)}
                                        className={`w-full px-3 py-2 rounded-lg text-xs font-bold transition-all border ${
                                          disabled
                                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                            : isPrimary
                                              ? 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                                              : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500 hover:bg-slate-50'
                                        }`}
                                      >
                                        {stageEvents.some((event) => event.action === action.action) ? `✓ ${action.label}` : action.label}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div className="mt-2 px-3 py-2 rounded-lg bg-white border text-xs font-bold text-slate-700">
                                  Duração: {formatDuration(duration)}
                                </div>
                              </div>
                            </div>

                            {stageEvents.length > 0 && (
                              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                {stageEvents.map((event) => (
                                  <div key={event.id} className="flex items-center justify-between rounded-lg bg-white/80 border border-slate-200 px-3 py-2">
                                    <div className="flex items-center gap-3">
                                      <span className="font-bold text-slate-700">{event.action}</span>
                                      <span className="text-slate-500">{formatEventTime(event.happenedAt)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => openEditEventModal(event)}
                                        className="text-xs px-2 py-1 bg-slate-100 rounded hover:bg-slate-200"
                                      >
                                        Editar
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-3">
                      <p className="text-xs font-bold text-slate-500 mb-2">Resumo da sala</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-lg bg-slate-50 p-2">
                          <span className="block text-slate-500">Paciente</span>
                          <span className="font-bold text-slate-900">{room.patientName || '—'}</span>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-2">
                          <span className="block text-slate-500">Procedimento</span>
                          <span className="font-bold text-slate-900">{room.procedureName || '—'}</span>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-2">
                          <span className="block text-slate-500">Cirurgião</span>
                          <span className="font-bold text-slate-900">{room.surgeonName || '—'}</span>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-2">
                          <span className="block text-slate-500">Tempo previsto de cirurgia</span>
                          <span className="font-bold text-slate-900">{room.estimatedMinutes ? `${room.estimatedMinutes} min` : '—'}</span>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-2">
                          <span className="block text-slate-500">Hora prevista</span>
                          <span className="font-bold text-slate-900">{room.scheduledStart || '—'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 p-3">
                      <p className="text-xs font-bold text-slate-500 mb-2">Histórico e movimentações</p>
                      <div className="space-y-2 max-h-64 overflow-auto pr-1">
                        {roomCaseEvents.map((event) => (
                          <div key={event.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
                            <div>
                              <p className="font-black text-slate-800">{event.eventKey}</p>
                              <p className="text-slate-500">{event.action} {event.auto ? '• auto' : '• manual'}</p>
                            </div>
                            <span className="font-bold text-slate-600">{formatEventTime(event.happenedAt)}</span>
                          </div>
                        ))}
                        {roomCaseEvents.length === 0 && (
                          <p className="text-slate-500 italic">Nenhuma movimentação registrada</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Event Modal */}
      {showEditEventModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-3">Editar horário do evento</h3>
            <label className="text-xs text-slate-500">Data e hora (UTC)</label>
            <input
              type="datetime-local"
              className="w-full p-2 border rounded mb-3"
              value={editEventTime}
              onChange={(e) => setEditEventTime(e.target.value)}
            />
            <div className="flex gap-2">
              <button className="flex-1 bg-slate-900 text-white py-2 rounded-lg font-bold hover:bg-slate-800" onClick={submitEditEvent}>Salvar</button>
              <button className="flex-1 bg-slate-100 text-slate-700 py-2 rounded-lg font-bold hover:bg-slate-200" onClick={() => setShowEditEventModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Room Modal */}
      {editingRoom && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
            <h3 className="text-lg font-bold mb-3">Editar Sala {editingRoom.code}</h3>
            <label className="text-xs text-slate-500">Nome</label>
            <input className="w-full p-2 border rounded mb-3" value={editValue} onChange={(e) => setEditValue(e.target.value)} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">Pesquisar paciente</label>
                <input
                  className="w-full p-2 border rounded mb-2"
                  placeholder="Nome, prontuário ou parte do nome"
                  value={editingRoomPatientQuery}
                  onChange={(e) => setEditingRoomPatientQuery(e.target.value)}
                />
                <div className="border rounded max-h-64 overflow-auto p-1">
                  {patients
                    .filter((p) => {
                      const q = editingRoomPatientQuery.trim().toLowerCase();
                      if (!q) return true;
                      return (p.fullName || '').toLowerCase().includes(q) || String(p.noticeNumber || '').includes(q);
                    })
                    .map((patient) => (
                      <div
                        key={patient.id}
                        onClick={() => setEditingRoomPatientId(patient.id)}
                        className={`p-2 rounded mb-1 cursor-pointer hover:bg-slate-100 flex justify-between items-center ${editingRoomPatientId === patient.id ? 'bg-slate-200 border border-slate-300' : ''}`}
                      >
                        <div>
                          <div className="font-bold">{patient.fullName}</div>
                          <div className="text-xs text-slate-500">{patient.noticeNumber ? `Prontuário: ${patient.noticeNumber}` : ''}</div>
                        </div>
                        {editingRoomPatientId === patient.id && (
                          <div className="text-xs text-slate-700 font-bold">Selecionado</div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Paciente selecionado</label>
                <div className="p-3 border rounded h-40">
                  {editingRoomPatientId ? (
                    (() => {
                      const p = patients.find((x) => x.id === editingRoomPatientId);
                      if (!p) return <div className="text-sm text-slate-500">Carregando...</div>;
                      return (
                        <div>
                          <div className="font-bold text-slate-900">{p.fullName}</div>
                          <div className="text-xs text-slate-500">{p.noticeNumber ? `Prontuário: ${p.noticeNumber}` : ''}</div>
                          <div className="text-sm mt-2">Procedimento: {p.procedureName || '—'}</div>
                          <div className="text-sm">Cirurgião: {p.surgeonName || '—'}</div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-sm text-slate-500">Nenhum paciente selecionado</div>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button className="flex-1 bg-slate-900 text-white py-2 rounded-lg font-bold hover:bg-slate-800" onClick={submitEditRoom}>Salvar</button>
                  <button className="flex-1 bg-slate-100 text-slate-700 py-2 rounded-lg font-bold hover:bg-slate-200" onClick={() => setEditingRoom(null)}>Cancelar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-3">Agendar paciente</h3>
            <label className="text-xs text-slate-500">Paciente</label>
            <select className="w-full p-2 border rounded mb-3" value={schedulePatientId || ''} onChange={(e) => setSchedulePatientId(e.target.value)}>
              <option value="">— selecione —</option>
              {patients.map((p) => (<option key={p.id} value={p.id}>{p.fullName} {p.noticeNumber ? `(${p.noticeNumber})` : ''}</option>))}
            </select>
            <label className="text-xs text-slate-500">Hora</label>
            <input type="time" className="w-full p-2 border rounded mb-3" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
            <div className="flex gap-2">
              <button className="flex-1 bg-slate-900 text-white py-2 rounded-lg font-bold hover:bg-slate-800" onClick={submitSchedule}>Agendar</button>
              <button className="flex-1 bg-slate-100 text-slate-700 py-2 rounded-lg font-bold hover:bg-slate-200" onClick={() => setShowScheduleModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de Justificativa de Atraso */}
      {showDelayPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-red-600" size={32} />
              <h2 className="text-xl font-bold text-slate-900">Justificar Atraso</h2>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              O paciente chegou com atraso. Por favor, selecione o motivo:
            </p>

            <div className="space-y-2 mb-6">
              {delayReasons.map((reason) => (
                <button
                  key={reason}
                  onClick={() => setDelayReason(reason)}
                  className={`w-full px-3 py-2 rounded-lg text-sm font-bold text-left transition-all ${
                    delayReason === reason
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                  }`}
                >
                  {reason}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleDelaySubmit}
                disabled={!delayReason}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-bold py-2 px-4 rounded-lg transition-all"
              >
                Confirmar
              </button>
              <button
                onClick={() => {
                  setShowDelayPopup(false);
                  setDelayReason('');
                }}
                className="flex-1 bg-slate-300 hover:bg-slate-400 text-slate-900 font-bold py-2 px-4 rounded-lg transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showCloseConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 border border-emerald-200">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-emerald-600" size={32} />
              <h2 className="text-xl font-bold text-slate-900">Finalizar tudo</h2>
            </div>
            <p className="text-sm text-slate-600 mb-2">
              Ao concluir, todas as etapas abertas desta sala serão finalizadas automaticamente.
            </p>
            <p className="text-sm text-slate-700 mb-6 font-bold">
              Sala: {closingRoom?.code || '—'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={confirmCloseCase}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded-lg transition-all"
              >
                Confirmar
              </button>
              <button
                onClick={() => {
                  setShowCloseConfirmModal(false);
                  setClosingRoom(null);
                  setClosingRoomMode(null);
                }}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold py-2 px-4 rounded-lg transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legenda de Status */}
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <p className="text-xs font-bold text-slate-600 mb-3">LEGENDA STATUS:</p>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-green-400 rounded"></span>
            <span className="text-xs">LIBERADO</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-yellow-400 rounded"></span>
            <span className="text-xs">EM PREPARO</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-blue-400 rounded"></span>
            <span className="text-xs">EM TRANSPORTE</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-red-400 rounded"></span>
            <span className="text-xs">EM ATRASO</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-slate-500 rounded"></span>
            <span className="text-xs">INICIADO CIRURGIA</span>
          </div>
        </div>
      </div>

      <VoiceCommandPanel
        stages={timelineStages}
        rooms={rooms.map((r) => ({ id: r.id, code: r.code, name: r.patientName || r.name, caseId: r.caseId }))}
        selectedRoomId={selectedRoom}
        onSelectRoom={(id) => setSelectedRoom(id)}
        checkAction={voiceCheckAction}
        runAction={voiceRunAction}
      />
    </div>
  );
}
